import { createContext, useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";

import { queryClient } from "../../../App";
import { StaticValue } from "../../seed/constants";
import { isUserAuthorizationToken } from "../../services/auth/authToken";
import { clearClaimSession } from "../../services/companies/claimSession";
import { useUserAccount } from "../../services/create-resume/queries";

const staticValue = new StaticValue();
const staticValues = staticValue.getDefaultValues();

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
	const navigate = useNavigate();

	const storedProfile = localStorage.getItem("profileData");
	const [userData, setUserData] = useState(storedProfile ? JSON.parse(storedProfile) : null);

	const [cookies, __, removeCookie] = useCookies(["Authorization"]);
	const { data, isLoading, refetch, error } = useUserAccount();

	useEffect(() => {
		if (!isUserAuthorizationToken(cookies.Authorization)) {
			setUserData(null);
		}
	}, [cookies.Authorization]);

	useEffect(() => {
		if (isLoading) return;
		if (data) {
			setUserData(data);
			const isLoggingIn = localStorage.getItem("isLoggingIn");
			if (isLoggingIn) {
				navigate("/profile");
				localStorage.removeItem("isLoggingIn");
			}
		} else if (error) {
			setUserData(null);
		}
	}, [data, isLoading, error]);

	useEffect(() => {
		if (!userData) {
			localStorage.removeItem("profileData");
			return;
		}
		localStorage.setItem("profileData", JSON.stringify(userData));
	}, [userData]);

	const clearUserData = () => {
		setUserData(null);
		clearClaimSession();
		// Not every logout path reloads the page — without this the next person to sign in in this
		// tab reads the previous account out of the react-query cache.
		queryClient.clear();
		localStorage.removeItem("profileData");
		localStorage.removeItem("isDropzOpsUser");
		localStorage.removeItem("jobPreferences");

		// The pointer is written with `path: "/"`, and a removal has to name the same
		// path or it targets the current directory instead and the cookie survives —
		// leaving the next person in this tab pointed at the previous resume.
		removeCookie(staticValues.localStorageResumeId, { path: "/" });

		const allLocalStorageKeys = Object.keys(localStorage);
		allLocalStorageKeys.forEach((key) => {
			if (key.includes("resume")) {
				localStorage.removeItem(key);
			}
		});
	};

	const value = {
		userData,
		setUserData,
		isLoading,
		refetch,
		logout: clearUserData,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
